import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Plus, ClipboardList, Camera, MapPin, Clock, ShieldAlert, ShieldCheck, X as XIcon, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";

const CATEGORIES = [
  { v: "near_miss", l: "Near Miss" },
  { v: "unsafe_act", l: "Unsafe Act" },
  { v: "unsafe_condition", l: "Unsafe Condition" },
  { v: "environmental", l: "Environmental" },
  { v: "property_damage", l: "Property Damage" },
];
const SEVERITIES = [
  { v: "low", l: "Low", color: "#059669" },
  { v: "medium", l: "Medium", color: "#D97706" },
  { v: "high", l: "High", color: "#EA580C" },
  { v: "critical", l: "Critical", color: "#DC2626" },
];
const STATUSES = [
  { v: "open", l: "Open" },
  { v: "under_review", l: "Under Review" },
  { v: "resolved", l: "Resolved" },
  { v: "closed", l: "Closed" },
];
const SEV_COLOR_MAP = Object.fromEntries(SEVERITIES.map(s => [s.v, s.color]));

export default function YellowCards() {
  const { user } = useAuth();
  const isReviewer = ["admin","super_admin"].includes(user?.role) || user?.role === "approver";
  const canSubmit = ["admin","super_admin"].includes(user?.role) || user?.role === "inspector";
  const [cards, setCards] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState({ status: "all", severity: "all", category: "all" });
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [statusForm, setStatusForm] = useState({ status: "under_review", resolution_notes: "", corrective_actions: "" });
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);

  function emptyForm() {
    return { title: "", description: "", location: "", asset_id: "", severity: "low", category: "near_miss",
      immediate_actions: "", root_cause: "", photo_ids: [], date: new Date().toISOString().slice(0, 10) };
  }

  const loadCards = useCallback(async () => {
    const params = {};
    if (filter.status !== "all") params.status = filter.status;
    if (filter.severity !== "all") params.severity = filter.severity;
    if (filter.category !== "all") params.category = filter.category;
    try { const { data } = await api.get("/yellow-cards", { params }); setCards(data); }
    catch (err) { console.debug("yellow-cards load failed", err?.message); toast.error("Failed to load yellow cards"); }
  }, [filter]);
  const loadAnalytics = useCallback(async () => {
    if (!isReviewer) return;
    try { const { data } = await api.get("/yellow-cards/analytics"); setAnalytics(data); } catch (err) { console.debug("yellow-card analytics failed", err?.message); }
  }, [isReviewer]);
  const loadAssets = useCallback(async () => {
    try { const { data } = await api.get("/assets"); setAssets(data); } catch (err) { console.debug("assets fetch failed", err?.message); }
  }, []);
  useEffect(() => { loadCards(); loadAnalytics(); loadAssets(); }, [loadCards, loadAnalytics, loadAssets]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads/photo`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (data.file_id) setForm(f => ({ ...f, photo_ids: [...f.photo_ids, data.file_id] }));
      else toast.error("Upload failed");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.location.trim()) return toast.error("Title, description and location are required");
    try {
      await api.post("/yellow-cards", { ...form, asset_id: form.asset_id || null });
      toast.success("Yellow card submitted");
      setNewOpen(false); setForm(emptyForm());
      loadCards(); loadAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (c) => {
    try { const { data } = await api.get(`/yellow-cards/${c.id}`); setDetail(data); setStatusForm({ status: data.status, resolution_notes: data.resolution_notes || "", corrective_actions: data.corrective_actions || "" }); }
    catch { toast.error("Failed to load"); }
  };

  const updateStatus = async () => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/yellow-cards/${detail.id}/status`, statusForm);
      toast.success(`Status updated to ${data.status}`);
      setDetail(data);
      loadCards(); loadAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this yellow card entirely?")) return;
    try { await api.delete(`/yellow-cards/${id}`); toast.success("Deleted"); setDetail(null); loadCards(); loadAnalytics(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="yellow-cards-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Safety</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <span className="w-9 h-9 bg-[#F59E0B] flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.5} /></span>
            Yellow Cards
          </h1>
          <p className="text-sm text-gray-500 mt-1">{isReviewer ? "Track every near-miss, unsafe act, and safety observation across the fleet." : "Submit a near-miss or unsafe condition so it can be reviewed and closed out."}</p>
        </div>
        {canSubmit && (
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild><Button data-testid="new-yc-btn" className="rounded-md bg-[#F59E0B] hover:bg-[#D97706] text-black"><Plus className="w-4 h-4 mr-2" />Report Near-Miss</Button></DialogTrigger>
            <NewYellowCardDialog form={form} setForm={setForm} assets={assets} onSubmit={submit} onClose={() => setNewOpen(false)} onUpload={uploadPhoto} uploading={uploading} />
          </Dialog>
        )}
      </div>

      {isReviewer && analytics && (
        <YCAnalytics data={analytics} />
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-end gap-3 mb-4">
        <div className="w-40">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Status</Label>
          <Select value={filter.status} onValueChange={v => setFilter({ ...filter, status: v })}><SelectTrigger className="rounded-md mt-1" data-testid="yc-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="w-40">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Severity</Label>
          <Select value={filter.severity} onValueChange={v => setFilter({ ...filter, severity: v })}><SelectTrigger className="rounded-md mt-1" data-testid="yc-filter-severity"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{SEVERITIES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="w-48">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Category</Label>
          <Select value={filter.category} onValueChange={v => setFilter({ ...filter, category: v })}><SelectTrigger className="rounded-md mt-1" data-testid="yc-filter-category"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      {/* Cards list */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Reference</Th><Th>Title</Th><Th>Location</Th><Th>Category</Th><Th>Severity</Th><Th>Submitted</Th><Th>Status</Th></tr></thead>
          <tbody data-testid="yc-table">
            {cards.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">{canSubmit ? "No yellow cards yet — submit one when you spot a hazard." : "No yellow cards match the current filters."}</td></tr> :
              cards.map(c => (
                <tr key={c.id} onClick={() => openDetail(c)} data-testid={`yc-row-${c.id}`} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <Td className="font-mono font-semibold">{c.reference_number}</Td>
                  <Td><div className="font-semibold truncate max-w-xs">{c.title}</div>{c.asset_name && <div className="text-xs text-gray-500">{c.asset_name} · {c.asset_tag}</div>}</Td>
                  <Td className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" />{c.location}</Td>
                  <Td className="capitalize">{c.category?.replace('_', ' ')}</Td>
                  <Td><SeverityPill s={c.severity} /></Td>
                  <Td><div className="text-xs">{c.submitted_at?.slice(0, 10)}</div><div className="text-[10px] text-gray-500">{c.submitted_by_name}</div></Td>
                  <Td><StatusPill s={c.status} /></Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-md max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 flex-wrap"><span className="font-mono text-lg">{detail?.reference_number}</span><SeverityPill s={detail?.severity} /><StatusPill s={detail?.status} /></DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Submitted by {detail?.submitted_by_name} on {detail?.submitted_at?.slice(0, 10)} · {detail?.category?.replace('_', ' ')}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 py-2">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Title</div>
                <div className="font-display text-lg font-bold mt-1">{detail.title}</div>
              </div>
              <Section title="Description">{detail.description}</Section>
              <div className="grid grid-cols-2 gap-4">
                <Section title="Location">{detail.location}</Section>
                <Section title="Date">{detail.date}</Section>
              </div>
              {detail.immediate_actions && <Section title="Immediate Actions">{detail.immediate_actions}</Section>}
              {detail.root_cause && <Section title="Root Cause">{detail.root_cause}</Section>}
              {detail.photo_ids?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">Photos</div>
                  <div className="grid grid-cols-3 gap-2">
                    {detail.photo_ids.map(pid => <a key={pid} href={`${API}/uploads/${pid}`} target="_blank" rel="noreferrer" className="block border border-gray-200 hover:border-[#01ADEF]"><img src={`${API}/uploads/${pid}`} alt="" className="w-full h-32 object-cover" /></a>)}
                  </div>
                </div>
              )}
              {detail.resolution_notes && <div className="border-l-4 border-[#059669] bg-[#F0FDF4] p-3"><div className="text-xs uppercase tracking-wider font-semibold text-[#065F46]">Resolution</div><p className="text-sm mt-1">{detail.resolution_notes}</p></div>}
              {detail.corrective_actions && <div className="border-l-4 border-[#01ADEF] bg-[#F0F7FF] p-3"><div className="text-xs uppercase tracking-wider font-semibold text-[#0198D6]">Corrective Actions</div><p className="text-sm mt-1">{detail.corrective_actions}</p></div>}

              {isReviewer && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-semibold">Update Status</div>
                  <div className="space-y-3">
                    <Select value={statusForm.status} onValueChange={v => setStatusForm({ ...statusForm, status: v })}><SelectTrigger className="rounded-md" data-testid="yc-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
                    <Textarea data-testid="yc-resolution-notes" value={statusForm.resolution_notes} onChange={e => setStatusForm({ ...statusForm, resolution_notes: e.target.value })} placeholder="Resolution notes…" className="rounded-md text-sm" />
                    <Textarea data-testid="yc-corrective-actions" value={statusForm.corrective_actions} onChange={e => setStatusForm({ ...statusForm, corrective_actions: e.target.value })} placeholder="Corrective actions to prevent recurrence…" className="rounded-md text-sm" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {["admin","super_admin"].includes(user?.role) && detail && <Button variant="outline" onClick={() => remove(detail.id)} className="rounded-md border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>}
            <Button variant="outline" className="rounded-full" onClick={() => setDetail(null)}>Close</Button>
            {isReviewer && detail && <Button data-testid="yc-update-status-btn" onClick={updateStatus} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Update Status</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewYellowCardDialog({ form, setForm, assets, onSubmit, onClose, onUpload, uploading }) {
  return (
    <DialogContent className="rounded-md max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#F59E0B]" />Report a Near-Miss</DialogTitle>
        <DialogDescription className="text-xs text-gray-500">A yellow card documents a hazard, near-miss, or unsafe condition so the safety team can review and mitigate.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <Field label="Title *"><Input data-testid="yc-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Loose railing on stairwell B" className="rounded-md" /></Field>
        <Field label="Description *"><Textarea data-testid="yc-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe what happened, who was involved, and any close-call outcome…" className="rounded-md min-h-[90px]" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Location *"><Input data-testid="yc-location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Site / area / zone" className="rounded-md" /></Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-md" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger className="rounded-md" data-testid="yc-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Severity">
            <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}><SelectTrigger className="rounded-md" data-testid="yc-severity"><SelectValue /></SelectTrigger><SelectContent>{SEVERITIES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <Field label="Related Asset (optional)">
          <Select value={form.asset_id || "none"} onValueChange={v => setForm({ ...form, asset_id: v === "none" ? "" : v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">— No specific asset —</SelectItem>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Immediate Actions Taken"><Textarea data-testid="yc-immediate" value={form.immediate_actions} onChange={e => setForm({ ...form, immediate_actions: e.target.value })} placeholder="What was done on the spot to mitigate?" className="rounded-md" /></Field>
        <Field label="Suspected Root Cause"><Textarea value={form.root_cause} onChange={e => setForm({ ...form, root_cause: e.target.value })} placeholder="Best guess at what caused this (optional)" className="rounded-md" /></Field>

        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700 flex items-center gap-1"><Camera className="w-3.5 h-3.5" />Photos</Label>
          <label className="mt-1 flex items-center justify-center border-2 border-dashed border-gray-300 p-3 cursor-pointer hover:border-[#F59E0B] transition-colors" data-testid="yc-photo-input">
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
            <span className="text-sm text-gray-600">{uploading ? "Uploading…" : "Add photo"}</span>
          </label>
          {form.photo_ids.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {form.photo_ids.map((pid, i) => (
                <div key={pid} className="relative group">
                  <img src={`${API}/uploads/${pid}`} alt="" className="w-20 h-20 object-cover border border-gray-200" />
                  <button type="button" onClick={() => setForm({ ...form, photo_ids: form.photo_ids.filter((_, idx) => idx !== i) })} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#DC2626] text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><XIcon className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
        <Button data-testid="yc-submit-btn" onClick={onSubmit} className="rounded-md bg-[#F59E0B] hover:bg-[#D97706] text-black">Submit Yellow Card</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function YCAnalytics({ data }) {
  const monthly = data.monthly.map(m => ({ ...m, month: m.month.slice(2) }));
  return (
    <div className="mb-6 space-y-4" data-testid="yc-analytics">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniKpi label="Total" value={data.kpis.total} accent="bg-[#01ADEF]" testid="yc-kpi-total" icon={ClipboardList} />
        <MiniKpi label="Open" value={data.kpis.open} accent="bg-[#F59E0B]" testid="yc-kpi-open" icon={AlertTriangle} />
        <MiniKpi label="Under Review" value={data.kpis.under_review} accent="bg-[#D97706]" testid="yc-kpi-review" icon={Clock} />
        <MiniKpi label="Resolved" value={data.kpis.resolved} accent="bg-[#059669]" testid="yc-kpi-resolved" icon={ShieldCheck} />
        <MiniKpi label="Critical Open" value={data.kpis.critical_open} accent="bg-[#DC2626]" testid="yc-kpi-critical" icon={ShieldAlert} />
        <MiniKpi label="Avg Close" value={fmtHours(data.kpis.avg_close_hours)} accent="bg-[#6B7280]" testid="yc-kpi-avg-close" icon={Clock} plain />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 p-5" data-testid="yc-monthly-chart">
          <h2 className="font-display text-lg font-bold tracking-tight mb-1">Monthly Trend</h2>
          <p className="text-xs text-gray-500 mb-3">Yellow cards submitted vs. resolved per month (last 12)</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
              <Legend />
              <Line type="monotone" dataKey="submitted" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="resolved" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="yc-severity-pie">
          <h2 className="font-display text-lg font-bold tracking-tight mb-1">By Severity</h2>
          <p className="text-xs text-gray-500 mb-3">All-time distribution</p>
          {data.by_severity.every(x => x.count === 0) ? (
            <div className="text-center py-14 text-sm text-gray-500">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.by_severity} dataKey="count" nameKey="severity" innerRadius={40} outerRadius={72}>
                  {data.by_severity.map((s) => <Cell key={`sev-${s.severity}`} fill={SEV_COLOR_MAP[s.severity] || "#6B7280"} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 p-5" data-testid="yc-category-bar">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">By Category</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="category" tick={{ fill: "#6B7280", fontSize: 11 }} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
              <Bar dataKey="count" fill="#F59E0B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="yc-top-locations">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">Top Locations</h2>
          {data.top_locations.length === 0 ? (
            <div className="text-center py-14 text-sm text-gray-500">No data.</div>
          ) : (
            <div className="space-y-2">
              {data.top_locations.map((l, idx) => (
                <div key={l.location} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1">
                  <span><span className="font-mono text-gray-400">#{idx + 1}</span> <span className="ml-2">{l.location}</span></span>
                  <span className="font-mono font-bold">{l.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">Top Reporters</h2>
          {data.top_submitters.length === 0 ? (
            <div className="text-center py-14 text-sm text-gray-500">No data.</div>
          ) : (
            <div className="space-y-2" data-testid="yc-top-submitters">
              {data.top_submitters.map((s, idx) => (
                <div key={s.name} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1">
                  <span><span className="font-mono text-gray-400">#{idx + 1}</span> <span className="ml-2">{s.name}</span></span>
                  <span className="font-mono font-bold">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtHours(h) {
  if (!h) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Section = ({ title, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">{title}</div>
    <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{children}</p>
  </div>
);
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const SeverityPill = ({ s }) => {
  const map = {
    low: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]"],
    medium: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]"],
    high: ["bg-[#FED7AA]", "text-[#9A3412]", "border-[#FDBA74]"],
    critical: ["bg-[#FEE2E2]", "text-[#B91C1C]", "border-[#FCA5A5]"],
  };
  if (!s) return null;
  const [bg, tx, br] = map[s] || map.low;
  return <span data-testid={`yc-sev-${s}`} className={`text-[10px] font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{s}</span>;
};
const StatusPill = ({ s }) => {
  const map = {
    open: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]"],
    under_review: ["bg-[#E0F2FE]", "text-[#075985]", "border-[#7DD3FC]"],
    resolved: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]"],
    closed: ["bg-gray-100", "text-gray-700", "border-gray-300"],
  };
  if (!s) return null;
  const [bg, tx, br] = map[s] || map.open;
  return <span data-testid={`yc-status-${s}`} className={`text-[10px] font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{s.replace("_", " ")}</span>;
};
const MiniKpi = ({ label, value, accent, testid, icon: Icon, plain }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className={`w-7 h-7 ${accent} flex items-center justify-center`}><Icon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className={`font-display font-bold tracking-tight ${plain ? "text-lg" : "text-2xl md:text-3xl"}`}>{value}</div>
  </div>
);
